import React, { useState } from 'react';
import { Button, colors, Icon, Modal, Skeleton, Tag } from '@neshane/blueberry';
import { startRegistration } from '@simplewebauthn/browser';
import { useSecurity } from '../hooks/useSecurity';
import { useStepUp } from '../hooks/useStepUp';
import {
    terminateSession,
    terminateAllSessions,
    deviceAction,
    disableTotp,
    removePasskey,
    getPasskeyRegistrationOptions,
    registerPasskey,
    generateRecoveryCodes,
    removeDastine,
} from '../api/securityApi';
import MfaSetupWizard from '../components/MfaSetupWizard';
import DastineSetupWizard from '../components/DastineSetupWizard';
import RecoveryCodesModal from '../components/RecoveryCodesModal';
import './SecurityCenterContainer.css';

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fa-IR') + ' ' + date.toLocaleTimeString('fa-IR');
};

const EVENT_TONES = {
    success: colors.contentSuccessDefault,
    info: colors.contentInfoDefault,
    warning: colors.contentWarningDefault,
    alert: colors.contentAlertDefault,
    neutral: colors.contentSecondaryBgSecondary,
};

const SecuritySkeleton = () => (
    <div className="security-center">
        {[0, 1, 2, 3].map((i) => (
            <div className="card security-card" key={i}>
                <Skeleton width="45%" height="22px" />
                <div className="security-skeleton__body">
                    <Skeleton width="100%" height="64px" />
                    <Skeleton width="100%" height="64px" />
                </div>
            </div>
        ))}
    </div>
);

const SecurityEmptyState = ({ iconName, title, description, action }) => (
    <div className="security-empty">
        <span className="security-empty__icon">
            <Icon name={iconName} size="xl" color={colors.contentSecondaryBgSecondary} />
        </span>
        <span className="security-empty__title">{title}</span>
        {description && <span className="security-empty__description">{description}</span>}
        {action}
    </div>
);

const SecurityFlash = ({ type, message }) => (
    <div className={`security-flash security-flash--${type}`} role="status">
        <Icon
            name={type === 'error' ? 'triangle-warning' : 'circle-check'}
            size="m"
            color={type === 'error' ? colors.contentAlertDefault : colors.contentSuccessDefault}
        />
        <span>{message}</span>
    </div>
);

const SecurityCard = ({ title, description, icon, iconTone, badge, actions, children }) => (
    <div className="card security-card">
        <div className="security-card__header">
            <div className="security-card__heading">
                {icon && (
                    <span className="security-card__icon" style={{ color: iconTone }}>
                        <Icon name={icon} size="m" />
                    </span>
                )}
                <div className="security-card__heading-text">
                    <h2>{title}</h2>
                    {description && <p>{description}</p>}
                </div>
            </div>
            <div className="security-card__header-actions">
                {badge}
                {actions}
            </div>
        </div>
        {children}
    </div>
);

const SecurityCenterContainer = () => {
    const {
        mfaEnabled,
        mfaMethods,
        sessions,
        sessionsLoading,
        devices,
        devicesLoading,
        passkeys,
        dastineCredentials,
        dastineLoading,
        recoveryCodesInfo,
        events,
        eventsLoading,
        isLoading,
        refreshAll,
    } = useSecurity();

    const { runWithStepUp } = useStepUp();

    const [actionError, setActionError] = useState(null);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [busyAction, setBusyAction] = useState(null);
    const [mfaWizardOpen, setMfaWizardOpen] = useState(false);
    const [dastineWizardOpen, setDastineWizardOpen] = useState(false);
    const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
    const [newRecoveryCodes, setNewRecoveryCodes] = useState(null);
    const [confirmTerminateAll, setConfirmTerminateAll] = useState(false);

    const flash = (msg, type = 'success') => {
        setActionError(type === 'error' ? msg : null);
        setActionSuccess(type === 'success' ? msg : null);
        setTimeout(() => {
            setActionError(null);
            setActionSuccess(null);
        }, 4000);
    };

    const runAction = async (key, fn) => {
        setBusyAction(key);
        setActionError(null);
        setActionSuccess(null);
        try {
            const result = await fn();
            refreshAll();
            if (result?.message) flash(result.message);
            return result;
        } catch (err) {
            if (err?.message === 'STEP_UP_CANCELLED') {
                flash('عملیات لغو شد', 'error');
            } else {
                flash(err?.response?.data?.error || err?.message || 'خطا در انجام عملیات', 'error');
            }
            return null;
        } finally {
            setBusyAction(null);
        }
    };

    const handleEnableTotp = () => {
        setMfaWizardOpen(true);
    };

    const handleDisableTotp = () => {
        runAction('disable-totp', () =>
            runWithStepUp('MFA_CHANGE', (token) => disableTotp(token)),
        );
    };

    const handleAddPasskey = async () => {
        const deviceName = `Passkey (${new Date().toLocaleDateString('fa-IR')})`;
        await runAction('add-passkey', async () => {
            const options = await getPasskeyRegistrationOptions();
            const registration = await startRegistration(options);
            return registerPasskey(registration, deviceName);
        });
    };

    const handleRemovePasskey = (passkeyId) => {
        runAction('remove-passkey', () =>
            runWithStepUp('PASSKEY_CHANGE', (token) => removePasskey(passkeyId, token)),
        );
    };

    const handleRemoveDastine = (dastineId) => {
        runAction('remove-dastine', () =>
            runWithStepUp('DASTINE_CHANGE', (token) => removeDastine(dastineId, token)),
        );
    };

    const handleGenerateRecoveryCodes = () => {
        runAction('recovery-codes', async () => {
            const result = await runWithStepUp('RECOVERY_CODES', (token) => generateRecoveryCodes(token));
            if (result?.codes) {
                setNewRecoveryCodes(result.codes);
                setRecoveryModalOpen(true);
            }
            return result;
        });
    };

    const handleTerminateAllSessions = () => {
        setConfirmTerminateAll(false);
        runAction('terminate-all', () =>
            runWithStepUp('TERMINATE_ALL_SESSIONS', (token) => terminateAllSessions(token)),
        );
    };

    const handleTerminateSession = (sessionId) => {
        runAction(`terminate-${sessionId}`, () => terminateSession(sessionId));
    };

    const handleDeviceAction = (deviceId, action) => {
        runAction(`device-${deviceId}-${action}`, () => deviceAction(deviceId, action));
    };

    if (isLoading) {
        return <SecuritySkeleton />;
    }

    const totpMethod = mfaMethods.find((m) => m.type === 'totp');

    return (
        <div className="security-center">
            {actionError && <SecurityFlash type="error" message={actionError} />}
            {actionSuccess && <SecurityFlash type="success" message={actionSuccess} />}

            {/* ------------------------------------------------------------------ */}
            {/* MFA Status */}
            {/* ------------------------------------------------------------------ */}
            <SecurityCard
                icon="shield-star"
                iconTone={mfaEnabled ? colors.contentSuccessDefault : colors.contentSecondaryBgSecondary}
                title="احراز هویت دو مرحله‌ای (MFA)"
                description="با فعال‌سازی MFA، امنیت حساب خود را در برابر دسترسی‌های غیرمجاز افزایش دهید."
                badge={
                    <Tag variant={mfaEnabled ? 'success' : 'neutral'} iconName={mfaEnabled ? 'circle-check' : 'lock-open'}>
                        {mfaEnabled ? 'فعال' : 'غیرفعال'}
                    </Tag>
                }
            >
                {mfaEnabled ? (
                    <div className="security-section">
                        <div className="security-methods">
                            {totpMethod && (
                                <div className="security-method-chip">
                                    <span className="security-method-chip__icon">
                                        <Icon name="lock-close" size="l" color={colors.contentBrandDefault} />
                                    </span>
                                    <div className="security-method-chip__info">
                                        <span className="security-method-chip__name">Google Authenticator</span>
                                        <span className="security-method-chip__detail">
                                            {totpMethod.name || 'کد یک بار مصرف'}
                                        </span>
                                    </div>
                                    <Button
                                        variant="secondaryAlert"
                                        text="غیرفعال‌سازی"
                                        iconName="lock-open"
                                        iconPosition="left"
                                        onClick={handleDisableTotp}
                                        isDisabled={busyAction === 'disable-totp'}
                                    />
                                </div>
                            )}

                            {passkeys.map((passkey) => (
                                <div className="security-method-chip" key={passkey.id}>
                                    <span className="security-method-chip__icon">
                                        <Icon name="key" size="l" color={colors.contentInfoDefault} />
                                    </span>
                                    <div className="security-method-chip__info">
                                        <span className="security-method-chip__name">{passkey.name}</span>
                                        <span className="security-method-chip__detail">
                                            آخرین استفاده: {formatDateTime(passkey.lastUsedAt)}
                                        </span>
                                    </div>
                                    <Button
                                        variant="secondaryNeutral"
                                        text="حذف"
                                        iconName="trash"
                                        iconPosition="left"
                                        onClick={() => handleRemovePasskey(passkey.id)}
                                        isDisabled={busyAction === 'remove-passkey'}
                                    />
                                </div>
                            ))}

                            <div className="security-method-actions">
                                <Button
                                    variant="primaryBrand"
                                    text="افزودن Passkey"
                                    iconName="plus"
                                    iconPosition="left"
                                    onClick={handleAddPasskey}
                                    isDisabled={busyAction === 'add-passkey'}
                                />
                                <Button
                                    variant="secondaryNeutral"
                                    text="تولید کدهای بازیابی"
                                    iconName="receipt-checked"
                                    iconPosition="left"
                                    onClick={handleGenerateRecoveryCodes}
                                    isDisabled={busyAction === 'recovery-codes'}
                                />
                                {recoveryCodesInfo && (
                                    <Tag variant="info" iconName="receipt-checked">
                                        {recoveryCodesInfo.remaining} کد باقی‌مانده
                                    </Tag>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <SecurityEmptyState
                        iconName="shield-star"
                        title="شما هنوز احراز هویت دو مرحله‌ای را فعال نکرده‌اید"
                        description="فعال‌سازی این قابلیت، محافظت بیشتری برای ورود به حساب شما فراهم می‌کند."
                        action={
                            <Button
                                variant="primaryBrand"
                                text="فعال‌سازی Google Authenticator"
                                iconName="lock-close"
                                iconPosition="left"
                                onClick={handleEnableTotp}
                            />
                        }
                    />
                )}
            </SecurityCard>

            {/* ------------------------------------------------------------------ */}
            {/* Dastine Credentials */}
            {/* ------------------------------------------------------------------ */}
            <SecurityCard
                icon="certificate"
                iconTone={colors.contentInfoDefault}
                title="گواهی‌های Dastine"
                description="گواهی‌های Dastine ثبت شده برای ورود بدون رمز عبور را مدیریت کنید."
                actions={
                    <Button
                        variant="primaryBrand"
                        text="ثبت گواهی جدید"
                        iconName="plus"
                        iconPosition="left"
                        onClick={() => setDastineWizardOpen(true)}
                        isDisabled={busyAction === 'add-dastine'}
                    />
                }
            >
                {dastineLoading ? (
                    <div className="security-list">
                        <div className="security-list__row">
                            <Skeleton width="100%" height="52px" />
                        </div>
                    </div>
                ) : dastineCredentials.length === 0 ? (
                    <SecurityEmptyState
                        iconName="certificate"
                        title="گواهی Dastine ثبت نشده است"
                        description="برای ورود بدون رمز عبور، یک گواهی Dastine ثبت کنید."
                        action={
                            <Button
                                variant="primaryBrand"
                                text="ثبت گواهی Dastine"
                                iconName="plus"
                                iconPosition="left"
                                onClick={() => setDastineWizardOpen(true)}
                            />
                        }
                    />
                ) : (
                    <div className="security-list">
                        {dastineCredentials.map((cred) => (
                            <div className="security-list__row" key={cred.id}>
                                <span className="security-list__icon">
                                    <Icon name="certificate" size="l" color={colors.contentInfoDefault} />
                                </span>
                                <div className="security-list__info">
                                    <span className="security-list__title">{cred.name || `Dastine #${cred.id}`}</span>
                                    <span className="security-list__subtitle">
                                        اثر انگشت: {cred.fingerprint ? `${String(cred.fingerprint).slice(0, 16)}…` : '-'} • ایجاد: {formatDateTime(cred.createdAt)}
                                    </span>
                                </div>
                                <Button
                                    variant="secondaryAlert"
                                    text="حذف"
                                    iconName="trash"
                                    iconPosition="left"
                                    onClick={() => handleRemoveDastine(cred.id)}
                                    isDisabled={busyAction === 'remove-dastine'}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </SecurityCard>

            {/* ------------------------------------------------------------------ */}
            {/* Sessions */}
            {/* ------------------------------------------------------------------ */}
            <SecurityCard
                icon="desktop"
                iconTone={colors.contentInfoDefault}
                title="نشست‌های فعال"
                description="دستگاه‌هایی که به حساب شما متصل هستند را مدیریت کنید."
                actions={
                    sessions.length > 1 && (
                        <Button
                            variant="secondaryAlert"
                            text="خروج از همه دستگاه‌ها"
                            iconName="arrow-right-rectangle"
                            iconPosition="left"
                            onClick={() => setConfirmTerminateAll(true)}
                            isDisabled={busyAction === 'terminate-all'}
                        />
                    )
                }
            >
                {sessionsLoading ? (
                    <div className="security-list">
                        <div className="security-list__row">
                            <Skeleton width="100%" height="52px" />
                        </div>
                        <div className="security-list__row">
                            <Skeleton width="100%" height="52px" />
                        </div>
                    </div>
                ) : sessions.length === 0 ? (
                    <SecurityEmptyState
                        iconName="desktop"
                        title="نشست فعالی وجود ندارد"
                        description="هیچ دستگاه دیگری به حساب شما متصل نیست."
                    />
                ) : (
                    <div className="security-list">
                        {sessions.map((session) => (
                            <div className="security-list__row" key={session.sessionId}>
                                <span className="security-list__icon">
                                    <Icon name="desktop" size="l" color={colors.contentSecondaryBgSecondary} />
                                </span>
                                <div className="security-list__info">
                                    <span className="security-list__title">
                                        {session.deviceName}
                                        {session.currentSession && (
                                            <Tag variant="success" iconName="circle-check">این دستگاه</Tag>
                                        )}
                                    </span>
                                    <span className="security-list__subtitle">
                                        {session.browser} • {session.os} • IP: {session.ipAddress}
                                        <br />
                                        آخرین فعالیت: {formatDateTime(session.lastSeenAt)}
                                    </span>
                                </div>
                                {!session.currentSession && (
                                    <Button
                                        variant="secondaryNeutral"
                                        text="قطع"
                                        iconName="cross"
                                        iconPosition="left"
                                        onClick={() => handleTerminateSession(session.sessionId)}
                                        isDisabled={busyAction === `terminate-${session.sessionId}`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </SecurityCard>

            {/* ------------------------------------------------------------------ */}
            {/* Trusted devices */}
            {/* ------------------------------------------------------------------ */}
            <SecurityCard
                icon="smartphone"
                iconTone={colors.contentBrandDefault}
                title="دستگاه‌های مورد اعتماد"
                description="مدیریت دستگاه‌هایی که به آن‌ها اعتماد داده‌اید."
            >
                {devicesLoading ? (
                    <div className="security-list">
                        <div className="security-list__row">
                            <Skeleton width="100%" height="52px" />
                        </div>
                        <div className="security-list__row">
                            <Skeleton width="100%" height="52px" />
                        </div>
                    </div>
                ) : devices.length === 0 ? (
                    <SecurityEmptyState
                        iconName="smartphone"
                        title="دستگاه‌ی ثبت نشده است"
                        description="هنگامی که از یک دستگاه جدید وارد شوید، اینجا ظاهر می‌شود."
                    />
                ) : (
                    <div className="security-list">
                        {devices.map((device) => (
                            <div className="security-list__row" key={device.id}>
                                <span className="security-list__icon">
                                    <Icon name="smartphone" size="l" color={colors.contentSecondaryBgSecondary} />
                                </span>
                                <div className="security-list__info">
                                    <span className="security-list__title">
                                        {device.deviceName}
                                        {device.trusted && (
                                            <Tag variant="success" iconName="circle-check">مورد اعتماد</Tag>
                                        )}
                                    </span>
                                    <span className="security-list__subtitle">
                                        {device.browser} • {device.os} • IP: {device.ipAddress}
                                        <br />
                                        آخرین فعالیت: {formatDateTime(device.lastSeenAt)}
                                    </span>
                                </div>
                                <div className="security-list__actions">
                                    {device.trusted ? (
                                        <Button
                                            variant="secondaryNeutral"
                                            text="لغو اعتماد"
                                            iconName="lock-open"
                                            iconPosition="left"
                                            onClick={() => handleDeviceAction(device.id, 'revoke')}
                                            isDisabled={busyAction === `device-${device.id}-revoke`}
                                        />
                                    ) : (
                                        <Button
                                            variant="primaryBrand"
                                            text="اعتماد"
                                            iconName="circle-check"
                                            iconPosition="left"
                                            onClick={() => handleDeviceAction(device.id, 'trust')}
                                            isDisabled={busyAction === `device-${device.id}-trust`}
                                        />
                                    )}
                                    <Button
                                        variant="secondaryAlert"
                                        text="حذف"
                                        iconName="trash"
                                        iconPosition="left"
                                        onClick={() => handleDeviceAction(device.id, 'remove')}
                                        isDisabled={busyAction === `device-${device.id}-remove`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </SecurityCard>

            {/* ------------------------------------------------------------------ */}
            {/* Login history / security events */}
            {/* ------------------------------------------------------------------ */}
            <SecurityCard
                icon="circle-information"
                iconTone={colors.contentInfoDefault}
                title="رویدادهای امنیتی"
                description="تاریخچه ورود و فعالیت‌های امنیتی حساب شما."
            >
                {eventsLoading ? (
                    <div className="security-events">
                        <div className="security-event">
                            <Skeleton width="100%" height="48px" />
                        </div>
                        <div className="security-event">
                            <Skeleton width="100%" height="48px" />
                        </div>
                    </div>
                ) : events.length === 0 ? (
                    <SecurityEmptyState
                        iconName="circle-information"
                        title="رویدادی ثبت نشده است"
                        description="فعالیت‌های امنیتی حساب شما اینجا نمایش داده می‌شود."
                    />
                ) : (
                    <div className="security-events">
                        {events.map((event) => (
                            <div className="security-event" key={event.id}>
                                <span
                                    className="security-event__icon"
                                    style={{ color: EVENT_TONES[event.getVariant()] || colors.contentSecondaryBgSecondary }}
                                >
                                    <Icon name={event.getIcon()} size="m" />
                                </span>
                                <div className="security-event__info">
                                    <span className="security-event__title">{event.getLabel()}</span>
                                    <span className="security-event__meta">
                                        {event.deviceName} • IP: {event.ipAddress}
                                    </span>
                                </div>
                                <span className="security-event__time">{formatDateTime(event.createdAt)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </SecurityCard>

            {/* Modals */}
            <MfaSetupWizard
                isOpen={mfaWizardOpen}
                onClose={() => setMfaWizardOpen(false)}
                onEnabled={() => {
                    setMfaWizardOpen(false);
                    refreshAll();
                    flash('احراز هویت دو مرحله‌ای فعال شد');
                }}
            />
            <DastineSetupWizard
                isOpen={dastineWizardOpen}
                onClose={() => setDastineWizardOpen(false)}
                onRegistered={() => {
                    refreshAll();
                    flash('گواهی Dastine با موفقیت ثبت شد');
                }}
            />
            {recoveryModalOpen && (
                <RecoveryCodesModal
                    isOpen={recoveryModalOpen}
                    onClose={() => setRecoveryModalOpen(false)}
                    codes={newRecoveryCodes || []}
                    remaining={recoveryCodesInfo?.remaining}
                />
            )}
            <Modal
                isOpen={confirmTerminateAll}
                onClose={() => setConfirmTerminateAll(false)}
                iconName="triangle-warning"
                iconVariant="warning"
                title="خروج از همه دستگاه‌ها"
                actions={[
                    <Button
                        key="cancel"
                        variant="secondaryNeutral"
                        text="انصراف"
                        onClick={() => setConfirmTerminateAll(false)}
                    />,
                    <Button
                        key="confirm"
                        variant="primaryAlert"
                        text="تأیید و خروج"
                        onClick={handleTerminateAllSessions}
                        isDisabled={busyAction === 'terminate-all'}
                    />,
                ]}
            >
                از بستن تمام جلسات دیگر اطمینان دارید؟ پس از این کار، باید روی این دستگاه‌ها دوباره وارد شوید.
            </Modal>
        </div>
    );
};

export default SecurityCenterContainer;
